// Fixed respondent-facing text on the call page (CampaignLandingPage.js) and the call card of
// the Test Call drawer (TestCallDrawer.js), in the interview language of the AGENT
// (multilingual voice survey, 2026-11-04). Plain dictionary, no library.
//
// - Keys: the agent `language` code (en, zh, ko, es, ru, bn, hi). Anything else, and any
//   missing key, falls back to English.
// - English is the source of truth and must stay byte-identical to the text the pages
//   showed before this file existed.
// - zh = Mandarin, Simplified characters. es = neutral US Latin American Spanish ("usted").
// - READY_LABEL (the "I'm ready to answer" button under media) is also what the agent says
//   aloud when it tells the participant to press that button, so the backend prompt copies
//   these exact strings. Change both together.

const EN = {
  // Buttons
  startCall: 'Start Call',
  connecting: 'Connecting...',
  hangup: 'Hangup',
  mute: 'Mute',
  unmute: 'Unmute',
  ready: "I'm ready to answer",
  waitSeconds: 'Wait {n}s',
  almost: 'Almost...',
  tapForSound: 'Tap for sound',
  next: 'Next',
  tryAgain: 'Try Again',
  callAgain: 'Call again',
  callByPhone: 'Call by Phone',
  callFromWeb: 'Call from Web',
  startFromBrowser: 'Start directly from your browser',
  or: 'Or',
  yourPin: 'Your PIN',
  pinBadge: 'PIN:',
  chooseHow: "Choose how you'd like to start the interview:",
  surveyFallback: 'Survey',

  // Statuses
  callInProgress: 'Call in progress',
  readyToCall: 'Ready to call',
  endingCall: 'Ending call...',
  processing: 'Processing...',
  loadingSurvey: 'Loading survey...',
  liveCall: 'Live Call',
  youAreMuted: 'You are muted',
  micMutedHelper: 'Your mic is muted. Press "{ready}" when done viewing.',
  mediaTipsPlayer: 'Replay it or skip to any part with the player controls. You can also ask the interviewer to show it again later.',
  mediaTipsOther: 'You can ask the interviewer to show it again later.',
  noise: "It seems there's too much disturbance - could you consider moving to a quieter place?",

  // End / refusal screens
  studyNotFound: 'Study Not Found',
  studyNotFoundBody: "The study you're looking for doesn't exist or has been removed.",
  linkTurnedOff: 'Link Turned Off',
  linkExpired: 'Link Expired',
  invalidLink: 'Invalid Link',
  surveyClosed: 'Survey Closed',
  pleaseTryAgain: 'Please Try Again',
  interviewNotAvailable: 'Interview Not Available',
  interviewNotAvailableMsg: 'This interview is not available.',
  openLinkFailed: 'We could not open this interview. Please check your connection and try again.',
  surveyClosedBody: 'Uh oh! You missed it. This survey has been closed and is no longer accepting responses.',
  surveyClosedThanks: 'Thank you for your interest in participating.',
  callEndedTitle: 'Your Call Ended',
  callEndedBeforeStart: 'Your call ended before the interview started. You can start the call again.',
  callEndedBeforePin: 'Your call ended before your PIN was confirmed. You can start the call again.',
  thankYou: 'Thank You',
  wrappingUp: 'Wrapping Up Your Interview',
  recordedClose: 'Your interview has been recorded. You can close this page now.',
  wrappingUpBody: 'This usually takes a minute or two. Please keep this page open and you will be taken back automatically.',
  alreadyCompleted: 'Already Completed',
  alreadyCompletedBody: 'You have already completed this survey. Thank you for your participation!',
  pinExpired: 'PIN Expired',
  noLongerAvailable: 'Sorry, this interview is no longer available.',
  linkExpiredBody: 'Uh oh! This interview link has expired. Interview links are valid for 1 hour after being opened.',
  pinExpiredBody: 'Uh oh! Your PIN has expired. Survey PINs are valid for 1 hour after being issued.',
  contactSupport: 'Please contact support if you believe this is an error.',
  invalidLinkBody: 'Uh oh! Looks like this survey link is no longer valid.',
  invalidLinkMore: 'The link may have expired or been removed. Please contact support if you believe this is an error.',

  // Notifications
  callError: 'Call Error',
  callErrorDesc: 'An error occurred during the call',
  cannotStartCall: 'Cannot Start Call',
  noAgentConfigured: 'No AI agent is configured for this study',
  openFromInvite: 'Please open this interview from your invitation link.',
  pinUpdated: 'Your PIN was updated',
  loadingNewPin: 'Loading your new PIN...',
  interviewUpdated: 'This interview was just updated',
  loadingLatest: 'Loading the latest version...',
  micRequired: 'Microphone Access Required',
  micRequiredDesc: 'Please allow microphone access to start the call',
  failedToStart: 'Failed to Start Call',
  couldNotConnect: 'Could not connect to the AI agent',
  error: 'Error',
  studyNotFoundMsg: 'Study not found',

  // Server refusal messages (open link join + web-call start). The page shows the server's
  // English text as-is for English; for other languages a known message is translated here.
  srvLinkNotValid: 'This link is not valid.',
  srvLinkOff: 'This link has been turned off.',
  srvLinkExpired: 'This link has expired.',
  srvStudyClosed: 'This study is closed.',
  srvBusy: 'Lots of people are joining right now. Please try again in a few minutes.',
  srvTryAgain: 'Please try again.',
  srvWentWrong: 'Something went wrong. Please try again.',
  srvInterviewLinkNotValid: 'This interview link is not valid.',
  srvStudyNotAvailable: 'This study is not available.',
  srvAgentNotReady: 'The interviewer is not ready yet. Please try again later.',
  srvCompleted: 'This interview has already been completed. Thank you!',
  srvLinkUsed: 'This interview link has already been used.',
  srvInterviewLinkExpired: 'This interview link has expired.',
  srvNoLonger: 'This interview is no longer available.',
};

const ZH = {
  startCall: '开始通话',
  connecting: '正在连接...',
  hangup: '挂断',
  mute: '静音',
  unmute: '取消静音',
  ready: '我准备好回答了',
  waitSeconds: '请等待 {n} 秒',
  almost: '马上就好...',
  tapForSound: '点击开启声音',
  next: '下一步',
  tryAgain: '重试',
  callAgain: '再次通话',
  callByPhone: '通过电话拨打',
  callFromWeb: '网页通话',
  startFromBrowser: '直接在浏览器中开始',
  or: '或',
  yourPin: '您的 PIN 码',
  pinBadge: 'PIN 码：',
  chooseHow: '请选择开始访谈的方式：',
  surveyFallback: '调查',

  callInProgress: '通话中',
  readyToCall: '可以开始通话',
  endingCall: '正在结束通话...',
  processing: '处理中...',
  loadingSurvey: '正在加载调查...',
  liveCall: '实时通话',
  youAreMuted: '您已静音',
  micMutedHelper: '您的麦克风已静音。看完后请点击“{ready}”。',
  mediaTipsPlayer: '您可以使用播放器控件重播或跳到任意位置，也可以稍后请访谈员再次展示。',
  mediaTipsOther: '您可以稍后请访谈员再次展示。',
  noise: '周围似乎比较嘈杂，可以换到安静一点的地方吗？',

  studyNotFound: '未找到调查',
  studyNotFoundBody: '您要找的调查不存在或已被删除。',
  linkTurnedOff: '链接已关闭',
  linkExpired: '链接已过期',
  invalidLink: '链接无效',
  surveyClosed: '调查已结束',
  pleaseTryAgain: '请重试',
  interviewNotAvailable: '访谈不可用',
  interviewNotAvailableMsg: '此访谈不可用。',
  openLinkFailed: '无法打开此访谈。请检查网络连接后重试。',
  surveyClosedBody: '很遗憾，您错过了。此调查已结束，不再接受回复。',
  surveyClosedThanks: '感谢您有意参与。',
  callEndedTitle: '通话已结束',
  callEndedBeforeStart: '访谈开始前通话已结束。您可以重新开始通话。',
  callEndedBeforePin: '您的 PIN 码确认前通话已结束。您可以重新开始通话。',
  thankYou: '谢谢',
  wrappingUp: '正在完成您的访谈',
  recordedClose: '您的访谈已记录。现在可以关闭此页面。',
  wrappingUpBody: '通常需要一两分钟。请保持此页面打开，系统会自动带您返回。',
  alreadyCompleted: '已完成',
  alreadyCompletedBody: '您已完成此调查。感谢您的参与！',
  pinExpired: 'PIN 码已过期',
  noLongerAvailable: '抱歉，此访谈已不再可用。',
  linkExpiredBody: '很抱歉，此访谈链接已过期。访谈链接自打开起 1 小时内有效。',
  pinExpiredBody: '很抱歉，您的 PIN 码已过期。调查 PIN 码自发放起 1 小时内有效。',
  contactSupport: '如果您认为这是错误，请联系客服。',
  invalidLinkBody: '很抱歉，此调查链接似乎已失效。',
  invalidLinkMore: '该链接可能已过期或已被删除。如果您认为这是错误，请联系客服。',

  callError: '通话出错',
  callErrorDesc: '通话过程中出现错误',
  cannotStartCall: '无法开始通话',
  noAgentConfigured: '此调查尚未配置 AI 访谈员',
  openFromInvite: '请通过您的邀请链接打开此访谈。',
  pinUpdated: '您的 PIN 码已更新',
  loadingNewPin: '正在加载新的 PIN 码...',
  interviewUpdated: '此访谈刚刚已更新',
  loadingLatest: '正在加载最新版本...',
  micRequired: '需要麦克风权限',
  micRequiredDesc: '请允许使用麦克风以开始通话',
  failedToStart: '通话启动失败',
  couldNotConnect: '无法连接 AI 访谈员',
  error: '错误',
  studyNotFoundMsg: '未找到调查',

  srvLinkNotValid: '此链接无效。',
  srvLinkOff: '此链接已关闭。',
  srvLinkExpired: '此链接已过期。',
  srvStudyClosed: '此调查已结束。',
  srvBusy: '目前加入的人很多，请几分钟后再试。',
  srvTryAgain: '请重试。',
  srvWentWrong: '出了点问题，请重试。',
  srvInterviewLinkNotValid: '此访谈链接无效。',
  srvStudyNotAvailable: '此调查不可用。',
  srvAgentNotReady: '访谈员尚未准备好，请稍后再试。',
  srvCompleted: '此访谈已完成。谢谢！',
  srvLinkUsed: '此访谈链接已被使用。',
  srvInterviewLinkExpired: '此访谈链接已过期。',
  srvNoLonger: '此访谈已不再可用。',
};

const KO = {
  startCall: '통화 시작',
  connecting: '연결 중...',
  hangup: '통화 종료',
  mute: '음소거',
  unmute: '음소거 해제',
  ready: '답변할 준비가 됐어요',
  waitSeconds: '{n}초 기다려 주세요',
  almost: '거의 다 됐어요...',
  tapForSound: '탭하여 소리 켜기',
  next: '다음',
  tryAgain: '다시 시도',
  callAgain: '다시 통화',
  callByPhone: '전화로 참여',
  callFromWeb: '웹으로 참여',
  startFromBrowser: '브라우저에서 바로 시작하세요',
  or: '또는',
  yourPin: 'PIN 번호',
  pinBadge: 'PIN:',
  chooseHow: '인터뷰 시작 방법을 선택하세요:',
  surveyFallback: '설문조사',

  callInProgress: '통화 중',
  readyToCall: '통화 준비 완료',
  endingCall: '통화 종료 중...',
  processing: '처리 중...',
  loadingSurvey: '설문을 불러오는 중...',
  liveCall: '실시간 통화',
  youAreMuted: '음소거 상태입니다',
  micMutedHelper: '마이크가 음소거되었습니다. 다 보신 후 "{ready}"를 눌러 주세요.',
  mediaTipsPlayer: '플레이어 컨트롤로 다시 재생하거나 원하는 부분으로 이동할 수 있습니다. 나중에 인터뷰 진행자에게 다시 보여 달라고 요청할 수도 있습니다.',
  mediaTipsOther: '나중에 인터뷰 진행자에게 다시 보여 달라고 요청할 수 있습니다.',
  noise: '주변이 많이 시끄러운 것 같아요. 조용한 곳으로 옮겨 주실 수 있을까요?',

  studyNotFound: '설문을 찾을 수 없습니다',
  studyNotFoundBody: '찾으시는 설문이 존재하지 않거나 삭제되었습니다.',
  linkTurnedOff: '링크가 비활성화되었습니다',
  linkExpired: '링크가 만료되었습니다',
  invalidLink: '유효하지 않은 링크',
  surveyClosed: '설문이 마감되었습니다',
  pleaseTryAgain: '다시 시도해 주세요',
  interviewNotAvailable: '인터뷰를 이용할 수 없습니다',
  interviewNotAvailableMsg: '이 인터뷰는 이용할 수 없습니다.',
  openLinkFailed: '인터뷰를 열 수 없습니다. 인터넷 연결을 확인한 후 다시 시도해 주세요.',
  surveyClosedBody: '아쉽게도 참여 기간이 지났습니다. 이 설문은 마감되어 더 이상 응답을 받지 않습니다.',
  surveyClosedThanks: '참여에 관심을 가져 주셔서 감사합니다.',
  callEndedTitle: '통화가 종료되었습니다',
  callEndedBeforeStart: '인터뷰가 시작되기 전에 통화가 종료되었습니다. 통화를 다시 시작할 수 있습니다.',
  callEndedBeforePin: 'PIN이 확인되기 전에 통화가 종료되었습니다. 통화를 다시 시작할 수 있습니다.',
  thankYou: '감사합니다',
  wrappingUp: '인터뷰를 마무리하는 중입니다',
  recordedClose: '인터뷰가 기록되었습니다. 이제 이 페이지를 닫으셔도 됩니다.',
  wrappingUpBody: '보통 1~2분 정도 걸립니다. 이 페이지를 열어 두시면 자동으로 이동합니다.',
  alreadyCompleted: '이미 완료됨',
  alreadyCompletedBody: '이미 이 설문을 완료하셨습니다. 참여해 주셔서 감사합니다!',
  pinExpired: 'PIN이 만료되었습니다',
  noLongerAvailable: '죄송합니다. 이 인터뷰는 더 이상 이용할 수 없습니다.',
  linkExpiredBody: '이 인터뷰 링크가 만료되었습니다. 인터뷰 링크는 연 후 1시간 동안 유효합니다.',
  pinExpiredBody: 'PIN이 만료되었습니다. 설문 PIN은 발급 후 1시간 동안 유효합니다.',
  contactSupport: '오류라고 생각되시면 고객 지원팀에 문의해 주세요.',
  invalidLinkBody: '이 설문 링크는 더 이상 유효하지 않은 것 같습니다.',
  invalidLinkMore: '링크가 만료되었거나 삭제되었을 수 있습니다. 오류라고 생각되시면 고객 지원팀에 문의해 주세요.',

  callError: '통화 오류',
  callErrorDesc: '통화 중 오류가 발생했습니다',
  cannotStartCall: '통화를 시작할 수 없습니다',
  noAgentConfigured: '이 설문에 AI 인터뷰 진행자가 설정되어 있지 않습니다',
  openFromInvite: '초대 링크를 통해 이 인터뷰를 열어 주세요.',
  pinUpdated: 'PIN이 업데이트되었습니다',
  loadingNewPin: '새 PIN을 불러오는 중...',
  interviewUpdated: '이 인터뷰가 방금 업데이트되었습니다',
  loadingLatest: '최신 버전을 불러오는 중...',
  micRequired: '마이크 권한이 필요합니다',
  micRequiredDesc: '통화를 시작하려면 마이크 사용을 허용해 주세요',
  failedToStart: '통화를 시작하지 못했습니다',
  couldNotConnect: 'AI 인터뷰 진행자에 연결할 수 없습니다',
  error: '오류',
  studyNotFoundMsg: '설문을 찾을 수 없습니다',

  srvLinkNotValid: '유효하지 않은 링크입니다.',
  srvLinkOff: '이 링크는 비활성화되었습니다.',
  srvLinkExpired: '이 링크는 만료되었습니다.',
  srvStudyClosed: '이 설문은 마감되었습니다.',
  srvBusy: '지금 참여하는 분이 많습니다. 몇 분 후에 다시 시도해 주세요.',
  srvTryAgain: '다시 시도해 주세요.',
  srvWentWrong: '문제가 발생했습니다. 다시 시도해 주세요.',
  srvInterviewLinkNotValid: '유효하지 않은 인터뷰 링크입니다.',
  srvStudyNotAvailable: '이 설문은 이용할 수 없습니다.',
  srvAgentNotReady: '인터뷰 진행자가 아직 준비되지 않았습니다. 잠시 후 다시 시도해 주세요.',
  srvCompleted: '이 인터뷰는 이미 완료되었습니다. 감사합니다!',
  srvLinkUsed: '이 인터뷰 링크는 이미 사용되었습니다.',
  srvInterviewLinkExpired: '이 인터뷰 링크는 만료되었습니다.',
  srvNoLonger: '이 인터뷰는 더 이상 이용할 수 없습니다.',
};

const ES = {
  startCall: 'Iniciar llamada',
  connecting: 'Conectando...',
  hangup: 'Colgar',
  mute: 'Silenciar',
  unmute: 'Activar micrófono',
  ready: 'Ya puedo responder',
  waitSeconds: 'Espere {n} s',
  almost: 'Casi listo...',
  tapForSound: 'Toque para activar el sonido',
  next: 'Siguiente',
  tryAgain: 'Intentar de nuevo',
  callAgain: 'Llamar de nuevo',
  callByPhone: 'Llamar por teléfono',
  callFromWeb: 'Llamar desde la web',
  startFromBrowser: 'Comience directamente desde su navegador',
  or: 'O',
  yourPin: 'Su PIN',
  pinBadge: 'PIN:',
  chooseHow: 'Elija cómo desea comenzar la entrevista:',
  surveyFallback: 'Encuesta',

  callInProgress: 'Llamada en curso',
  readyToCall: 'Listo para llamar',
  endingCall: 'Finalizando la llamada...',
  processing: 'Procesando...',
  loadingSurvey: 'Cargando la encuesta...',
  liveCall: 'Llamada en vivo',
  youAreMuted: 'Su micrófono está silenciado',
  micMutedHelper: 'Su micrófono está silenciado. Cuando termine de ver, presione "{ready}".',
  mediaTipsPlayer: 'Puede volver a reproducirlo o ir a cualquier parte con los controles del reproductor. También puede pedirle al entrevistador que se lo muestre de nuevo más tarde.',
  mediaTipsOther: 'Puede pedirle al entrevistador que se lo muestre de nuevo más tarde.',
  noise: 'Parece que hay mucho ruido. ¿Podría ir a un lugar más tranquilo?',

  studyNotFound: 'Estudio no encontrado',
  studyNotFoundBody: 'El estudio que busca no existe o fue eliminado.',
  linkTurnedOff: 'Enlace desactivado',
  linkExpired: 'Enlace vencido',
  invalidLink: 'Enlace no válido',
  surveyClosed: 'Encuesta cerrada',
  pleaseTryAgain: 'Intente de nuevo',
  interviewNotAvailable: 'Entrevista no disponible',
  interviewNotAvailableMsg: 'Esta entrevista no está disponible.',
  openLinkFailed: 'No pudimos abrir esta entrevista. Revise su conexión e intente de nuevo.',
  surveyClosedBody: '¡Ay! Llegó tarde. Esta encuesta ya cerró y no acepta más respuestas.',
  surveyClosedThanks: 'Gracias por su interés en participar.',
  callEndedTitle: 'Su llamada terminó',
  callEndedBeforeStart: 'Su llamada terminó antes de que comenzara la entrevista. Puede volver a iniciar la llamada.',
  callEndedBeforePin: 'Su llamada terminó antes de que se confirmara su PIN. Puede volver a iniciar la llamada.',
  thankYou: 'Gracias',
  wrappingUp: 'Finalizando su entrevista',
  recordedClose: 'Su entrevista quedó registrada. Ya puede cerrar esta página.',
  wrappingUpBody: 'Esto suele tardar uno o dos minutos. Mantenga esta página abierta y volverá automáticamente.',
  alreadyCompleted: 'Ya completada',
  alreadyCompletedBody: 'Ya completó esta encuesta. ¡Gracias por participar!',
  pinExpired: 'PIN vencido',
  noLongerAvailable: 'Lo sentimos, esta entrevista ya no está disponible.',
  linkExpiredBody: '¡Ay! Este enlace de entrevista venció. Los enlaces de entrevista son válidos por 1 hora después de abrirse.',
  pinExpiredBody: '¡Ay! Su PIN venció. Los PIN de la encuesta son válidos por 1 hora después de emitirse.',
  contactSupport: 'Si cree que se trata de un error, comuníquese con soporte.',
  invalidLinkBody: '¡Ay! Parece que este enlace de la encuesta ya no es válido.',
  invalidLinkMore: 'Es posible que el enlace haya vencido o se haya eliminado. Si cree que se trata de un error, comuníquese con soporte.',

  callError: 'Error en la llamada',
  callErrorDesc: 'Ocurrió un error durante la llamada',
  cannotStartCall: 'No se puede iniciar la llamada',
  noAgentConfigured: 'Este estudio no tiene un agente de IA configurado',
  openFromInvite: 'Abra esta entrevista desde su enlace de invitación.',
  pinUpdated: 'Su PIN se actualizó',
  loadingNewPin: 'Cargando su nuevo PIN...',
  interviewUpdated: 'Esta entrevista se acaba de actualizar',
  loadingLatest: 'Cargando la versión más reciente...',
  micRequired: 'Se requiere acceso al micrófono',
  micRequiredDesc: 'Permita el acceso al micrófono para iniciar la llamada',
  failedToStart: 'No se pudo iniciar la llamada',
  couldNotConnect: 'No se pudo conectar con el agente de IA',
  error: 'Error',
  studyNotFoundMsg: 'Estudio no encontrado',

  srvLinkNotValid: 'Este enlace no es válido.',
  srvLinkOff: 'Este enlace fue desactivado.',
  srvLinkExpired: 'Este enlace venció.',
  srvStudyClosed: 'Este estudio está cerrado.',
  srvBusy: 'Hay muchas personas entrando en este momento. Intente de nuevo en unos minutos.',
  srvTryAgain: 'Intente de nuevo.',
  srvWentWrong: 'Algo salió mal. Intente de nuevo.',
  srvInterviewLinkNotValid: 'Este enlace de entrevista no es válido.',
  srvStudyNotAvailable: 'Este estudio no está disponible.',
  srvAgentNotReady: 'El entrevistador todavía no está listo. Intente de nuevo más tarde.',
  srvCompleted: 'Esta entrevista ya se completó. ¡Gracias!',
  srvLinkUsed: 'Este enlace de entrevista ya se usó.',
  srvInterviewLinkExpired: 'Este enlace de entrevista venció.',
  srvNoLonger: 'Esta entrevista ya no está disponible.',
};

const RU = {
  startCall: 'Начать звонок',
  connecting: 'Подключение...',
  hangup: 'Завершить',
  mute: 'Выключить звук',
  unmute: 'Включить звук',
  ready: 'Перейти к ответу',
  waitSeconds: 'Подождите {n} с',
  almost: 'Почти готово...',
  tapForSound: 'Нажмите, чтобы включить звук',
  next: 'Далее',
  tryAgain: 'Попробовать снова',
  callAgain: 'Позвонить снова',
  callByPhone: 'Позвонить по телефону',
  callFromWeb: 'Звонок через браузер',
  startFromBrowser: 'Начните прямо в браузере',
  or: 'Или',
  yourPin: 'Ваш PIN-код',
  pinBadge: 'PIN-код:',
  chooseHow: 'Выберите, как начать интервью:',
  surveyFallback: 'Опрос',

  callInProgress: 'Идет звонок',
  readyToCall: 'Готово к звонку',
  endingCall: 'Завершение звонка...',
  processing: 'Обработка...',
  loadingSurvey: 'Загрузка опроса...',
  liveCall: 'Идет звонок',
  youAreMuted: 'Ваш микрофон выключен',
  micMutedHelper: 'Ваш микрофон выключен. Когда закончите просмотр, нажмите «{ready}».',
  mediaTipsPlayer: 'Можно пересмотреть или перейти к любому моменту с помощью кнопок плеера. Позже вы также можете попросить интервьюера показать это снова.',
  mediaTipsOther: 'Позже вы можете попросить интервьюера показать это снова.',
  noise: 'Похоже, вокруг слишком шумно. Не могли бы вы перейти в более тихое место?',

  studyNotFound: 'Исследование не найдено',
  studyNotFoundBody: 'Исследование, которое вы ищете, не существует или было удалено.',
  linkTurnedOff: 'Ссылка отключена',
  linkExpired: 'Срок действия ссылки истек',
  invalidLink: 'Недействительная ссылка',
  surveyClosed: 'Опрос закрыт',
  pleaseTryAgain: 'Попробуйте снова',
  interviewNotAvailable: 'Интервью недоступно',
  interviewNotAvailableMsg: 'Это интервью недоступно.',
  openLinkFailed: 'Не удалось открыть интервью. Проверьте подключение к интернету и попробуйте снова.',
  surveyClosedBody: 'Увы, вы не успели. Этот опрос закрыт и больше не принимает ответы.',
  surveyClosedThanks: 'Спасибо за интерес к участию.',
  callEndedTitle: 'Звонок завершен',
  callEndedBeforeStart: 'Звонок завершился до начала интервью. Вы можете позвонить снова.',
  callEndedBeforePin: 'Звонок завершился до подтверждения PIN-кода. Вы можете позвонить снова.',
  thankYou: 'Спасибо',
  wrappingUp: 'Завершаем ваше интервью',
  recordedClose: 'Ваше интервью записано. Теперь эту страницу можно закрыть.',
  wrappingUpBody: 'Обычно это занимает одну-две минуты. Не закрывайте эту страницу, и вы автоматически вернетесь обратно.',
  alreadyCompleted: 'Уже пройдено',
  alreadyCompletedBody: 'Вы уже прошли этот опрос. Спасибо за участие!',
  pinExpired: 'Срок действия PIN-кода истек',
  noLongerAvailable: 'К сожалению, это интервью больше недоступно.',
  linkExpiredBody: 'Увы, срок действия этой ссылки на интервью истек. Ссылки на интервью действуют 1 час после открытия.',
  pinExpiredBody: 'Увы, срок действия вашего PIN-кода истек. PIN-коды опроса действуют 1 час после выдачи.',
  contactSupport: 'Если вы считаете, что это ошибка, обратитесь в службу поддержки.',
  invalidLinkBody: 'Увы, похоже, эта ссылка на опрос больше не действует.',
  invalidLinkMore: 'Возможно, срок действия ссылки истек или она была удалена. Если вы считаете, что это ошибка, обратитесь в службу поддержки.',

  callError: 'Ошибка звонка',
  callErrorDesc: 'Во время звонка произошла ошибка',
  cannotStartCall: 'Не удается начать звонок',
  noAgentConfigured: 'Для этого исследования не настроен ИИ-интервьюер',
  openFromInvite: 'Откройте это интервью по ссылке из приглашения.',
  pinUpdated: 'Ваш PIN-код обновлен',
  loadingNewPin: 'Загружаем новый PIN-код...',
  interviewUpdated: 'Интервью только что обновлено',
  loadingLatest: 'Загружаем последнюю версию...',
  micRequired: 'Нужен доступ к микрофону',
  micRequiredDesc: 'Разрешите доступ к микрофону, чтобы начать звонок',
  failedToStart: 'Не удалось начать звонок',
  couldNotConnect: 'Не удалось подключиться к ИИ-интервьюеру',
  error: 'Ошибка',
  studyNotFoundMsg: 'Исследование не найдено',

  srvLinkNotValid: 'Эта ссылка недействительна.',
  srvLinkOff: 'Эта ссылка отключена.',
  srvLinkExpired: 'Срок действия этой ссылки истек.',
  srvStudyClosed: 'Это исследование закрыто.',
  srvBusy: 'Сейчас подключается много людей. Попробуйте снова через несколько минут.',
  srvTryAgain: 'Попробуйте снова.',
  srvWentWrong: 'Что-то пошло не так. Попробуйте снова.',
  srvInterviewLinkNotValid: 'Эта ссылка на интервью недействительна.',
  srvStudyNotAvailable: 'Это исследование недоступно.',
  srvAgentNotReady: 'Интервьюер еще не готов. Попробуйте позже.',
  srvCompleted: 'Это интервью уже пройдено. Спасибо!',
  srvLinkUsed: 'Эта ссылка на интервью уже использована.',
  srvInterviewLinkExpired: 'Срок действия этой ссылки на интервью истек.',
  srvNoLonger: 'Это интервью больше недоступно.',
};

const BN = {
  startCall: 'কল শুরু করুন',
  connecting: 'সংযোগ করা হচ্ছে...',
  hangup: 'কল কাটুন',
  mute: 'মিউট',
  unmute: 'আনমিউট',
  ready: 'আমি উত্তর দিতে প্রস্তুত',
  waitSeconds: '{n} সেকেন্ড অপেক্ষা করুন',
  almost: 'প্রায় হয়ে গেছে...',
  tapForSound: 'শব্দ চালু করতে ট্যাপ করুন',
  next: 'পরবর্তী',
  tryAgain: 'আবার চেষ্টা করুন',
  callAgain: 'আবার কল করুন',
  callByPhone: 'ফোনে কল করুন',
  callFromWeb: 'ওয়েব থেকে কল করুন',
  startFromBrowser: 'সরাসরি আপনার ব্রাউজার থেকে শুরু করুন',
  or: 'অথবা',
  yourPin: 'আপনার পিন',
  pinBadge: 'পিন:',
  chooseHow: 'কীভাবে সাক্ষাৎকার শুরু করতে চান তা বেছে নিন:',
  surveyFallback: 'জরিপ',

  callInProgress: 'কল চলছে',
  readyToCall: 'কলের জন্য প্রস্তুত',
  endingCall: 'কল শেষ হচ্ছে...',
  processing: 'প্রক্রিয়া চলছে...',
  loadingSurvey: 'জরিপ লোড হচ্ছে...',
  liveCall: 'লাইভ কল',
  youAreMuted: 'আপনি মিউট আছেন',
  micMutedHelper: 'আপনার মাইক্রোফোন মিউট করা আছে। দেখা শেষ হলে "{ready}" চাপুন।',
  mediaTipsPlayer: 'প্লেয়ারের কন্ট্রোল দিয়ে আবার চালাতে বা যেকোনো অংশে যেতে পারেন। পরে সাক্ষাৎকারগ্রহীতাকে আবার দেখাতেও বলতে পারেন।',
  mediaTipsOther: 'পরে সাক্ষাৎকারগ্রহীতাকে আবার দেখাতে বলতে পারেন।',
  noise: 'মনে হচ্ছে চারপাশে খুব বেশি শব্দ হচ্ছে। একটু শান্ত জায়গায় যেতে পারবেন?',

  studyNotFound: 'স্টাডি পাওয়া যায়নি',
  studyNotFoundBody: 'আপনি যে স্টাডিটি খুঁজছেন সেটি নেই বা সরিয়ে ফেলা হয়েছে।',
  linkTurnedOff: 'লিংক বন্ধ করা হয়েছে',
  linkExpired: 'লিংকের মেয়াদ শেষ',
  invalidLink: 'অবৈধ লিংক',
  surveyClosed: 'জরিপ বন্ধ',
  pleaseTryAgain: 'অনুগ্রহ করে আবার চেষ্টা করুন',
  interviewNotAvailable: 'সাক্ষাৎকারটি উপলব্ধ নয়',
  interviewNotAvailableMsg: 'এই সাক্ষাৎকারটি উপলব্ধ নয়।',
  openLinkFailed: 'সাক্ষাৎকারটি খোলা যায়নি। আপনার ইন্টারনেট সংযোগ পরীক্ষা করে আবার চেষ্টা করুন।',
  surveyClosedBody: 'দুঃখিত, সময় পেরিয়ে গেছে। এই জরিপটি বন্ধ হয়ে গেছে এবং আর কোনো উত্তর নেওয়া হচ্ছে না।',
  surveyClosedThanks: 'অংশগ্রহণে আগ্রহ দেখানোর জন্য ধন্যবাদ।',
  callEndedTitle: 'আপনার কল শেষ হয়েছে',
  callEndedBeforeStart: 'সাক্ষাৎকার শুরু হওয়ার আগেই আপনার কল শেষ হয়ে গেছে। আপনি আবার কল শুরু করতে পারেন।',
  callEndedBeforePin: 'আপনার পিন নিশ্চিত হওয়ার আগেই কল শেষ হয়ে গেছে। আপনি আবার কল শুরু করতে পারেন।',
  thankYou: 'ধন্যবাদ',
  wrappingUp: 'আপনার সাক্ষাৎকার শেষ করা হচ্ছে',
  recordedClose: 'আপনার সাক্ষাৎকার রেকর্ড করা হয়েছে। এখন আপনি এই পেজটি বন্ধ করতে পারেন।',
  wrappingUpBody: 'সাধারণত এক-দুই মিনিট লাগে। এই পেজটি খোলা রাখুন, আপনাকে স্বয়ংক্রিয়ভাবে ফিরিয়ে নেওয়া হবে।',
  alreadyCompleted: 'ইতিমধ্যে সম্পন্ন',
  alreadyCompletedBody: 'আপনি ইতিমধ্যে এই জরিপটি সম্পন্ন করেছেন। অংশগ্রহণের জন্য ধন্যবাদ!',
  pinExpired: 'পিনের মেয়াদ শেষ',
  noLongerAvailable: 'দুঃখিত, এই সাক্ষাৎকারটি আর উপলব্ধ নেই।',
  linkExpiredBody: 'দুঃখিত, এই সাক্ষাৎকারের লিংকের মেয়াদ শেষ হয়ে গেছে। সাক্ষাৎকারের লিংক খোলার পর ১ ঘণ্টা পর্যন্ত বৈধ থাকে।',
  pinExpiredBody: 'দুঃখিত, আপনার পিনের মেয়াদ শেষ হয়ে গেছে। জরিপের পিন দেওয়ার পর ১ ঘণ্টা পর্যন্ত বৈধ থাকে।',
  contactSupport: 'যদি মনে করেন এটি একটি ত্রুটি, তাহলে সাপোর্টে যোগাযোগ করুন।',
  invalidLinkBody: 'দুঃখিত, মনে হচ্ছে এই জরিপের লিংকটি আর বৈধ নয়।',
  invalidLinkMore: 'লিংকটির মেয়াদ শেষ হয়ে থাকতে পারে বা এটি সরিয়ে ফেলা হয়ে থাকতে পারে। যদি মনে করেন এটি একটি ত্রুটি, তাহলে সাপোর্টে যোগাযোগ করুন।',

  callError: 'কলে সমস্যা',
  callErrorDesc: 'কলের সময় একটি সমস্যা হয়েছে',
  cannotStartCall: 'কল শুরু করা যাচ্ছে না',
  noAgentConfigured: 'এই স্টাডির জন্য কোনো এআই এজেন্ট সেট করা নেই',
  openFromInvite: 'অনুগ্রহ করে আপনার আমন্ত্রণ লিংক থেকে এই সাক্ষাৎকারটি খুলুন।',
  pinUpdated: 'আপনার পিন আপডেট করা হয়েছে',
  loadingNewPin: 'আপনার নতুন পিন লোড হচ্ছে...',
  interviewUpdated: 'এই সাক্ষাৎকারটি এইমাত্র আপডেট করা হয়েছে',
  loadingLatest: 'সর্বশেষ সংস্করণ লোড হচ্ছে...',
  micRequired: 'মাইক্রোফোনের অনুমতি প্রয়োজন',
  micRequiredDesc: 'কল শুরু করতে মাইক্রোফোন ব্যবহারের অনুমতি দিন',
  failedToStart: 'কল শুরু করা যায়নি',
  couldNotConnect: 'এআই এজেন্টের সাথে সংযোগ করা যায়নি',
  error: 'ত্রুটি',
  studyNotFoundMsg: 'স্টাডি পাওয়া যায়নি',

  srvLinkNotValid: 'এই লিংকটি বৈধ নয়।',
  srvLinkOff: 'এই লিংকটি বন্ধ করা হয়েছে।',
  srvLinkExpired: 'এই লিংকের মেয়াদ শেষ হয়ে গেছে।',
  srvStudyClosed: 'এই স্টাডিটি বন্ধ।',
  srvBusy: 'এই মুহূর্তে অনেকে যোগ দিচ্ছেন। কয়েক মিনিট পরে আবার চেষ্টা করুন।',
  srvTryAgain: 'অনুগ্রহ করে আবার চেষ্টা করুন।',
  srvWentWrong: 'কিছু একটা সমস্যা হয়েছে। আবার চেষ্টা করুন।',
  srvInterviewLinkNotValid: 'এই সাক্ষাৎকারের লিংকটি বৈধ নয়।',
  srvStudyNotAvailable: 'এই স্টাডিটি উপলব্ধ নয়।',
  srvAgentNotReady: 'সাক্ষাৎকারগ্রহীতা এখনও প্রস্তুত নন। পরে আবার চেষ্টা করুন।',
  srvCompleted: 'এই সাক্ষাৎকারটি ইতিমধ্যে সম্পন্ন হয়েছে। ধন্যবাদ!',
  srvLinkUsed: 'এই সাক্ষাৎকারের লিংকটি ইতিমধ্যে ব্যবহার করা হয়েছে।',
  srvInterviewLinkExpired: 'এই সাক্ষাৎকারের লিংকের মেয়াদ শেষ হয়ে গেছে।',
  srvNoLonger: 'এই সাক্ষাৎকারটি আর উপলব্ধ নেই।',
};

const HI = {
  startCall: 'कॉल शुरू करें',
  connecting: 'कनेक्ट हो रहा है...',
  hangup: 'कॉल काटें',
  mute: 'म्यूट',
  unmute: 'अनम्यूट',
  ready: 'मैं जवाब देने के लिए तैयार हूँ',
  waitSeconds: '{n} सेकंड रुकें',
  almost: 'बस थोड़ा और...',
  tapForSound: 'आवाज़ के लिए टैप करें',
  next: 'आगे',
  tryAgain: 'फिर से कोशिश करें',
  callAgain: 'फिर से कॉल करें',
  callByPhone: 'फ़ोन से कॉल करें',
  callFromWeb: 'वेब से कॉल करें',
  startFromBrowser: 'सीधे अपने ब्राउज़र से शुरू करें',
  or: 'या',
  yourPin: 'आपका पिन',
  pinBadge: 'पिन:',
  chooseHow: 'चुनें कि आप इंटरव्यू कैसे शुरू करना चाहते हैं:',
  surveyFallback: 'सर्वे',

  callInProgress: 'कॉल चल रही है',
  readyToCall: 'कॉल के लिए तैयार',
  endingCall: 'कॉल समाप्त हो रही है...',
  processing: 'प्रोसेस हो रहा है...',
  loadingSurvey: 'सर्वे लोड हो रहा है...',
  liveCall: 'लाइव कॉल',
  youAreMuted: 'आप म्यूट हैं',
  micMutedHelper: 'आपका माइक म्यूट है। देखना पूरा होने पर "{ready}" दबाएँ।',
  mediaTipsPlayer: 'प्लेयर कंट्रोल से इसे दोबारा चला सकते हैं या किसी भी हिस्से पर जा सकते हैं। आप बाद में इंटरव्यूअर से इसे फिर से दिखाने के लिए भी कह सकते हैं।',
  mediaTipsOther: 'आप बाद में इंटरव्यूअर से इसे फिर से दिखाने के लिए कह सकते हैं।',
  noise: 'लगता है आसपास बहुत शोर है। क्या आप किसी शांत जगह पर जा सकते हैं?',

  studyNotFound: 'स्टडी नहीं मिली',
  studyNotFoundBody: 'आप जिस स्टडी को ढूंढ रहे हैं, वह मौजूद नहीं है या हटा दी गई है।',
  linkTurnedOff: 'लिंक बंद कर दिया गया है',
  linkExpired: 'लिंक की समय-सीमा समाप्त',
  invalidLink: 'अमान्य लिंक',
  surveyClosed: 'सर्वे बंद है',
  pleaseTryAgain: 'कृपया फिर से कोशिश करें',
  interviewNotAvailable: 'इंटरव्यू उपलब्ध नहीं है',
  interviewNotAvailableMsg: 'यह इंटरव्यू उपलब्ध नहीं है।',
  openLinkFailed: 'हम यह इंटरव्यू नहीं खोल सके। कृपया अपना इंटरनेट कनेक्शन जांचें और फिर से कोशिश करें।',
  surveyClosedBody: 'ओह! आप चूक गए। यह सर्वे बंद हो चुका है और अब जवाब स्वीकार नहीं कर रहा है।',
  surveyClosedThanks: 'भाग लेने में रुचि दिखाने के लिए धन्यवाद।',
  callEndedTitle: 'आपकी कॉल समाप्त हो गई',
  callEndedBeforeStart: 'इंटरव्यू शुरू होने से पहले ही आपकी कॉल समाप्त हो गई। आप फिर से कॉल शुरू कर सकते हैं।',
  callEndedBeforePin: 'आपका पिन कन्फ़र्म होने से पहले ही कॉल समाप्त हो गई। आप फिर से कॉल शुरू कर सकते हैं।',
  thankYou: 'धन्यवाद',
  wrappingUp: 'आपका इंटरव्यू पूरा किया जा रहा है',
  recordedClose: 'आपका इंटरव्यू रिकॉर्ड हो गया है। अब आप यह पेज बंद कर सकते हैं।',
  wrappingUpBody: 'इसमें आमतौर पर एक-दो मिनट लगते हैं। कृपया यह पेज खुला रखें, आपको अपने-आप वापस ले जाया जाएगा।',
  alreadyCompleted: 'पहले ही पूरा हो चुका है',
  alreadyCompletedBody: 'आप यह सर्वे पहले ही पूरा कर चुके हैं। भाग लेने के लिए धन्यवाद!',
  pinExpired: 'पिन की समय-सीमा समाप्त',
  noLongerAvailable: 'क्षमा करें, यह इंटरव्यू अब उपलब्ध नहीं है।',
  linkExpiredBody: 'ओह! इस इंटरव्यू लिंक की समय-सीमा समाप्त हो गई है। इंटरव्यू लिंक खोलने के बाद 1 घंटे तक मान्य रहते हैं।',
  pinExpiredBody: 'ओह! आपके पिन की समय-सीमा समाप्त हो गई है। सर्वे पिन जारी होने के बाद 1 घंटे तक मान्य रहते हैं।',
  contactSupport: 'अगर आपको लगता है कि यह गलती है, तो कृपया सपोर्ट से संपर्क करें।',
  invalidLinkBody: 'ओह! लगता है यह सर्वे लिंक अब मान्य नहीं है।',
  invalidLinkMore: 'हो सकता है लिंक की समय-सीमा समाप्त हो गई हो या उसे हटा दिया गया हो। अगर आपको लगता है कि यह गलती है, तो कृपया सपोर्ट से संपर्क करें।',

  callError: 'कॉल में त्रुटि',
  callErrorDesc: 'कॉल के दौरान एक त्रुटि हुई',
  cannotStartCall: 'कॉल शुरू नहीं हो सकती',
  noAgentConfigured: 'इस स्टडी के लिए कोई AI एजेंट सेट नहीं है',
  openFromInvite: 'कृपया यह इंटरव्यू अपने निमंत्रण लिंक से खोलें।',
  pinUpdated: 'आपका पिन अपडेट हो गया है',
  loadingNewPin: 'आपका नया पिन लोड हो रहा है...',
  interviewUpdated: 'यह इंटरव्यू अभी अपडेट हुआ है',
  loadingLatest: 'नवीनतम संस्करण लोड हो रहा है...',
  micRequired: 'माइक्रोफ़ोन की अनुमति ज़रूरी है',
  micRequiredDesc: 'कॉल शुरू करने के लिए कृपया माइक्रोफ़ोन की अनुमति दें',
  failedToStart: 'कॉल शुरू नहीं हो सकी',
  couldNotConnect: 'AI एजेंट से कनेक्ट नहीं हो सका',
  error: 'त्रुटि',
  studyNotFoundMsg: 'स्टडी नहीं मिली',

  srvLinkNotValid: 'यह लिंक मान्य नहीं है।',
  srvLinkOff: 'यह लिंक बंद कर दिया गया है।',
  srvLinkExpired: 'इस लिंक की समय-सीमा समाप्त हो गई है।',
  srvStudyClosed: 'यह स्टडी बंद है।',
  srvBusy: 'अभी बहुत से लोग जुड़ रहे हैं। कृपया कुछ मिनट बाद फिर से कोशिश करें।',
  srvTryAgain: 'कृपया फिर से कोशिश करें।',
  srvWentWrong: 'कुछ गलत हो गया। कृपया फिर से कोशिश करें।',
  srvInterviewLinkNotValid: 'यह इंटरव्यू लिंक मान्य नहीं है।',
  srvStudyNotAvailable: 'यह स्टडी उपलब्ध नहीं है।',
  srvAgentNotReady: 'इंटरव्यूअर अभी तैयार नहीं है। कृपया बाद में फिर से कोशिश करें।',
  srvCompleted: 'यह इंटरव्यू पहले ही पूरा हो चुका है। धन्यवाद!',
  srvLinkUsed: 'यह इंटरव्यू लिंक पहले ही इस्तेमाल हो चुका है।',
  srvInterviewLinkExpired: 'इस इंटरव्यू लिंक की समय-सीमा समाप्त हो गई है।',
  srvNoLonger: 'यह इंटरव्यू अब उपलब्ध नहीं है।',
};

const MR = {
  startCall: 'कॉल सुरू करा',
  connecting: 'कनेक्ट होत आहे...',
  hangup: 'कॉल बंद करा',
  mute: 'म्यूट',
  unmute: 'अनम्यूट',
  ready: 'मी उत्तर देण्यासाठी तयार आहे',
  waitSeconds: '{n} सेकंद थांबा',
  almost: 'जवळजवळ झाले...',
  tapForSound: 'आवाजासाठी टॅप करा',
  next: 'पुढे',
  tryAgain: 'पुन्हा प्रयत्न करा',
  callAgain: 'पुन्हा कॉल करा',
  callByPhone: 'फोनवरून कॉल करा',
  callFromWeb: 'वेबवरून कॉल करा',
  startFromBrowser: 'थेट तुमच्या ब्राउझरमधून सुरू करा',
  or: 'किंवा',
  yourPin: 'तुमचा पिन',
  pinBadge: 'पिन:',
  chooseHow: 'मुलाखत कशी सुरू करायची ते निवडा:',
  surveyFallback: 'सर्वेक्षण',

  callInProgress: 'कॉल सुरू आहे',
  readyToCall: 'कॉलसाठी तयार',
  endingCall: 'कॉल संपत आहे...',
  processing: 'प्रक्रिया सुरू आहे...',
  loadingSurvey: 'सर्वेक्षण लोड होत आहे...',
  liveCall: 'लाइव्ह कॉल',
  youAreMuted: 'तुम्ही म्यूट आहात',
  micMutedHelper: 'तुमचा माइक म्यूट आहे. पाहून झाल्यावर "{ready}" दाबा.',
  mediaTipsPlayer: 'प्लेअर कंट्रोल वापरून तुम्ही हे पुन्हा चालवू शकता किंवा कोणत्याही भागावर जाऊ शकता. नंतर मुलाखतकाराला हे पुन्हा दाखवायलाही सांगू शकता.',
  mediaTipsOther: 'नंतर तुम्ही मुलाखतकाराला हे पुन्हा दाखवायला सांगू शकता.',
  noise: 'आजूबाजूला खूप आवाज आहे असे दिसते. तुम्ही एखाद्या शांत ठिकाणी जाऊ शकाल का?',

  studyNotFound: 'अभ्यास सापडला नाही',
  studyNotFoundBody: 'तुम्ही शोधत असलेला अभ्यास अस्तित्वात नाही किंवा काढून टाकला गेला आहे.',
  linkTurnedOff: 'लिंक बंद केली आहे',
  linkExpired: 'लिंकची मुदत संपली',
  invalidLink: 'अवैध लिंक',
  surveyClosed: 'सर्वेक्षण बंद आहे',
  pleaseTryAgain: 'कृपया पुन्हा प्रयत्न करा',
  interviewNotAvailable: 'मुलाखत उपलब्ध नाही',
  interviewNotAvailableMsg: 'ही मुलाखत उपलब्ध नाही.',
  openLinkFailed: 'आम्ही ही मुलाखत उघडू शकलो नाही. कृपया तुमचे इंटरनेट कनेक्शन तपासा आणि पुन्हा प्रयत्न करा.',
  surveyClosedBody: 'अरेरे! तुमची संधी हुकली. हे सर्वेक्षण बंद झाले आहे आणि आता उत्तरे स्वीकारत नाही.',
  surveyClosedThanks: 'सहभागी होण्यात रस दाखवल्याबद्दल धन्यवाद.',
  callEndedTitle: 'तुमचा कॉल संपला',
  callEndedBeforeStart: 'मुलाखत सुरू होण्यापूर्वीच तुमचा कॉल संपला. तुम्ही पुन्हा कॉल सुरू करू शकता.',
  callEndedBeforePin: 'तुमचा पिन निश्चित होण्यापूर्वीच कॉल संपला. तुम्ही पुन्हा कॉल सुरू करू शकता.',
  thankYou: 'धन्यवाद',
  wrappingUp: 'तुमची मुलाखत पूर्ण केली जात आहे',
  recordedClose: 'तुमची मुलाखत रेकॉर्ड झाली आहे. आता तुम्ही हे पेज बंद करू शकता.',
  wrappingUpBody: 'यासाठी साधारणपणे एक-दोन मिनिटे लागतात. कृपया हे पेज उघडे ठेवा, तुम्हाला आपोआप परत नेले जाईल.',
  alreadyCompleted: 'आधीच पूर्ण झाले आहे',
  alreadyCompletedBody: 'तुम्ही हे सर्वेक्षण आधीच पूर्ण केले आहे. सहभागी झाल्याबद्दल धन्यवाद!',
  pinExpired: 'पिनची मुदत संपली',
  noLongerAvailable: 'क्षमस्व, ही मुलाखत आता उपलब्ध नाही.',
  linkExpiredBody: 'अरेरे! या मुलाखतीच्या लिंकची मुदत संपली आहे. मुलाखतीच्या लिंक उघडल्यानंतर 1 तास वैध असतात.',
  pinExpiredBody: 'अरेरे! तुमच्या पिनची मुदत संपली आहे. सर्वेक्षण पिन दिल्यानंतर 1 तास वैध असतात.',
  contactSupport: 'ही चूक आहे असे वाटत असल्यास, कृपया सपोर्टशी संपर्क साधा.',
  invalidLinkBody: 'अरेरे! ही सर्वेक्षण लिंक आता वैध नाही असे दिसते.',
  invalidLinkMore: 'कदाचित लिंकची मुदत संपली असेल किंवा ती काढून टाकली गेली असेल. ही चूक आहे असे वाटत असल्यास, कृपया सपोर्टशी संपर्क साधा.',

  callError: 'कॉलमध्ये त्रुटी',
  callErrorDesc: 'कॉल दरम्यान एक त्रुटी आली',
  cannotStartCall: 'कॉल सुरू होऊ शकत नाही',
  noAgentConfigured: 'या अभ्यासासाठी कोणताही AI एजंट सेट केलेला नाही',
  openFromInvite: 'कृपया ही मुलाखत तुमच्या निमंत्रण लिंकवरून उघडा.',
  pinUpdated: 'तुमचा पिन अपडेट झाला आहे',
  loadingNewPin: 'तुमचा नवीन पिन लोड होत आहे...',
  interviewUpdated: 'ही मुलाखत नुकतीच अपडेट झाली आहे',
  loadingLatest: 'नवीनतम आवृत्ती लोड होत आहे...',
  micRequired: 'मायक्रोफोनची परवानगी आवश्यक आहे',
  micRequiredDesc: 'कॉल सुरू करण्यासाठी कृपया मायक्रोफोनची परवानगी द्या',
  failedToStart: 'कॉल सुरू होऊ शकला नाही',
  couldNotConnect: 'AI एजंटशी कनेक्ट होऊ शकलो नाही',
  error: 'त्रुटी',
  studyNotFoundMsg: 'अभ्यास सापडला नाही',

  srvLinkNotValid: 'ही लिंक वैध नाही.',
  srvLinkOff: 'ही लिंक बंद केली आहे.',
  srvLinkExpired: 'या लिंकची मुदत संपली आहे.',
  srvStudyClosed: 'हा अभ्यास बंद आहे.',
  srvBusy: 'सध्या बरेच लोक जोडले जात आहेत. कृपया काही मिनिटांनी पुन्हा प्रयत्न करा.',
  srvTryAgain: 'कृपया पुन्हा प्रयत्न करा.',
  srvWentWrong: 'काहीतरी चुकले. कृपया पुन्हा प्रयत्न करा.',
  srvInterviewLinkNotValid: 'ही मुलाखत लिंक वैध नाही.',
  srvStudyNotAvailable: 'हा अभ्यास उपलब्ध नाही.',
  srvAgentNotReady: 'मुलाखतकार अद्याप तयार नाही. कृपया नंतर पुन्हा प्रयत्न करा.',
  srvCompleted: 'ही मुलाखत आधीच पूर्ण झाली आहे. धन्यवाद!',
  srvLinkUsed: 'ही मुलाखत लिंक आधीच वापरली गेली आहे.',
  srvInterviewLinkExpired: 'या मुलाखत लिंकची मुदत संपली आहे.',
  srvNoLonger: 'ही मुलाखत आता उपलब्ध नाही.',
};

const DICT = { en: EN, zh: ZH, ko: KO, es: ES, ru: RU, bn: BN, hi: HI, mr: MR };

// The language codes this module has text for.
export const CALL_LANGS = Object.keys(DICT);

// 'zh-CN' / ' ES ' / null -> 'zh' / 'es' / 'en'. A code without text here falls back to English.
export const callLang = (code) => {
  const c = String(code || '').trim().toLowerCase().split(/[-_]/)[0];
  return DICT[c] ? c : 'en';
};

// Value for <html lang> / a lang attribute (Simplified Chinese gets its script subtag so
// browsers pick Simplified glyphs).
export const callHtmlLang = (code) => {
  const c = callLang(code);
  return c === 'zh' ? 'zh-Hans' : c;
};

// t('es', 'waitSeconds', { n: 5 }) -> 'Espere 5 s'. Unknown language or key -> English
// (an unknown key returns the key itself, so a typo shows up on screen instead of nothing).
export const t = (lang, key, vars) => {
  const table = DICT[callLang(lang)];
  let text = table[key] !== undefined ? table[key] : (EN[key] !== undefined ? EN[key] : key);
  if (vars) text = text.replace(/\{(\w+)\}/g, (m, name) => (vars[name] !== undefined ? String(vars[name]) : m));
  return text;
};

// The "I'm ready to answer" button label per language. The backend prompt must tell the
// participant to press exactly these words.
export const READY_LABEL = Object.fromEntries(CALL_LANGS.map((c) => [c, DICT[c].ready]));

// Known English server messages (open link join, web-call start) -> key.
const SERVER_KEYS = Object.fromEntries(
  Object.keys(EN)
    .filter((k) => k.startsWith('srv') || k === 'interviewNotAvailableMsg' || k === 'openLinkFailed')
    .map((k) => [EN[k], k])
);

// A message the server (or an English fallback) produced: English shows it unchanged; other
// languages get the translation of a known message, or the original text when unknown.
// Empty text -> the fallback key.
export const tServer = (lang, text, fallbackKey) => {
  if (!text) return t(lang, fallbackKey);
  if (callLang(lang) === 'en') return text;
  const key = SERVER_KEYS[text];
  return key ? t(lang, key) : text;
};

// A technical error text (SDK / network) with a translated fallback: English keeps today's
// behavior (the raw message, else the fallback); other languages show the translated
// fallback, since the raw text is English and technical (it is still logged to the console).
export const tError = (lang, rawMessage, fallbackKey) => (
  callLang(lang) === 'en' ? (rawMessage || EN[fallbackKey]) : t(lang, fallbackKey)
);
